import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { getImageKey } from '../offerGalleryImageUtils'
import { GALLERY_CONTENT_MAX_WIDTH } from './GalleryModalV2.types'

const LAYOUT_COUNTS = { mosaic: 3, mosaicReverse: 3, pair: 2, single: 1 } as const
const LAYOUT_PATTERN: Array<'mosaic' | 'pair' | 'mosaicReverse'> = ['mosaic', 'pair', 'mosaicReverse', 'pair']

const HALF_WIDTH_SIZES = `min(50vw, ${GALLERY_CONTENT_MAX_WIDTH / 2}px)`

interface GallerySection {
  type: keyof typeof LAYOUT_COUNTS
  images: Array<App.Image>
  startIndex: number
}

const GridRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  padding-bottom: ${rem(40)};
  width: 100%;
  min-width: 0;
  max-width: 100%;
`

const ImageFrame = styled(Clickable)`
  display: block;
  overflow: hidden;
  position: relative;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  min-width: 0;
  min-height: 0;
  max-width: 100%;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const PairRow = styled.div`
  display: flex;
  gap: ${rem(4)};
  width: 100%;
  min-width: 0;
  min-height: 0;
`

const PairFrame = styled(ImageFrame)`
  flex: 1;
  min-width: 0;
  aspect-ratio: 1 / 1;
`

const SingleFrame = styled(ImageFrame)`
  width: 50%;
  aspect-ratio: 1 / 1;
`

const MosaicRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${rem(4)};
  aspect-ratio: 16 / 9;
  width: 100%;
  min-width: 0;
  min-height: 0;
`

const MosaicStackedFrame = styled(ImageFrame)<{ row: 1 | 2, reverse?: boolean }>`
  grid-column: ${props => props.reverse ? 2 : 1};
  grid-row: ${props => props.row};
`

const MosaicTallFrame = styled(ImageFrame)<{ reverse?: boolean }>`
  grid-column: ${props => props.reverse ? 1 : 2};
  grid-row: 1 / 3;
`

function GridImage(props: { image: App.Image, index: number, sizes: string }) {
  const { image, index, sizes } = props
  const intl = useIntl()
  const alt = image.title ?? intl.formatMessage(
    {
      id: 'kQX15J_6ShAyEiCC_6Pp',
      defaultMessage: 'Image {index}',
      description: 'Fallback alt text for a gallery thumbnail image when no title is available',
    },
    { index: index + 1 },
  )

  if (image.id) {
    return <ResponsiveImage id={image.id} alt={alt} quality="good" fit="center" sizes={sizes} loading="lazy" />
  }
  if (image.url) {
    return <Image image={image} alt={alt} fit="center" loading="lazy" />
  }
  return null
}

interface Props {
  images: Array<App.Image>
  onImageClick: (images: Array<App.Image>, index: number) => void
}

function GalleryV2PhotoGrid(props: Props) {
  const { images, onImageClick } = props

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const index = Number(event.currentTarget.dataset.index)
    onImageClick(images, index)
  }, [images, onImageClick])

  const sections = useMemo(() => {
    const result: Array<GallerySection> = []
    let index = 0
    let patternIndex = 0

    while (index < images.length) {
      const remaining = images.length - index
      const patternType = LAYOUT_PATTERN[patternIndex % LAYOUT_PATTERN.length]
      const type = remaining >= LAYOUT_COUNTS[patternType]
        ? patternType
        : remaining === 2 ? 'pair' : 'single'

      const count = LAYOUT_COUNTS[type]
      result.push({ type, images: images.slice(index, index + count), startIndex: index })
      index += count
      patternIndex += 1
    }

    return result
  }, [images])

  return <GridRoot>
    {sections.map(section => {
      const { type, images: sectionImages, startIndex } = section
      const key = getImageKey(sectionImages[0], startIndex)

      if (type === 'single') {
        const [image] = sectionImages
        return <SingleFrame key={key} data-index={startIndex} data-image-key={getImageKey(image, startIndex)} onClick={handleClick}>
          <GridImage image={image} index={startIndex} sizes={HALF_WIDTH_SIZES} />
        </SingleFrame>
      }

      if (type === 'mosaic' || type === 'mosaicReverse') {
        const reverse = type === 'mosaicReverse'
        const [stackedTop, stackedBottom, tall] = sectionImages
        return <MosaicRow key={key}>
          <MosaicStackedFrame row={1} reverse={reverse} data-index={startIndex} data-image-key={getImageKey(stackedTop, startIndex)} onClick={handleClick}>
            <GridImage image={stackedTop} index={startIndex} sizes={HALF_WIDTH_SIZES} />
          </MosaicStackedFrame>
          <MosaicStackedFrame row={2} reverse={reverse} data-index={startIndex + 1} data-image-key={getImageKey(stackedBottom, startIndex + 1)} onClick={handleClick}>
            <GridImage image={stackedBottom} index={startIndex + 1} sizes={HALF_WIDTH_SIZES} />
          </MosaicStackedFrame>
          <MosaicTallFrame reverse={reverse} data-index={startIndex + 2} data-image-key={getImageKey(tall, startIndex + 2)} onClick={handleClick}>
            <GridImage image={tall} index={startIndex + 2} sizes={HALF_WIDTH_SIZES} />
          </MosaicTallFrame>
        </MosaicRow>
      }

      return <PairRow key={key}>
        {sectionImages.map((image, pairIndex) => (
          <PairFrame
            key={getImageKey(image, startIndex + pairIndex)}
            data-index={startIndex + pairIndex}
            data-image-key={getImageKey(image, startIndex + pairIndex)}
            onClick={handleClick}
          >
            <GridImage image={image} index={startIndex + pairIndex} sizes={HALF_WIDTH_SIZES} />
          </PairFrame>
        ))}
      </PairRow>
    })}
  </GridRoot>
}

export default GalleryV2PhotoGrid
